/*
 * Copyright (c) 2020. World Wide Generation Ltd
 */

import { Button } from 'reactstrap';
import { getGroup, getSubGroupPathByTags } from '@g17eco/core';
import { SurveyActionData } from '@g17eco/types/surveyAction';
import { useHistory, useLocation } from 'react-router-dom';
import UniversalTrackerClassInterface from '../../../model/UniversalTrackerClassInterface';

// WFN only for now; add standard codes here to roll the pillar path out further.
const STANDARDS_WITH_PILLAR_PATH = ['wfn_2024'];

interface BreadcrumbsProps {
  utr?: UniversalTrackerClassInterface;
  survey: SurveyActionData;
  selectedAltCode: string;
  rootBreadcrumb: {
    label: string;
    url: string;
  };
}

export const QuestionContainerHeaderBreadcrumbs = (props: BreadcrumbsProps) => {
  const { utr, selectedAltCode, rootBreadcrumb } = props;

  const history = useHistory();
  const location = useLocation();

  if (!utr) {
    return null;
  }

  const goToOverview = () => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.delete('standard'); // Don't carry the standard around
    history.push({
      pathname: rootBreadcrumb.url,
      search: searchParams.toString(),
    });
  };

  const standardCode = utr.getTypeCode(selectedAltCode);
  const standardName = utr.getTypeName(selectedAltCode);
  // Mirrors getTypeName: the metric's own standard when it has no data for the selected one.
  const standardType = utr.hasAlternativeInfo(selectedAltCode) ? selectedAltCode : utr.getType();
  const standard = STANDARDS_WITH_PILLAR_PATH.includes(standardType)
    ? getGroup('standards-and-frameworks', standardType)
    : undefined;
  // Same tags the overview groups by, so the path here matches its headings.
  const subGroupPath = standard
    ? getSubGroupPathByTags(standard, [...utr.getTypeTags(), ...utr.getAltTypeTags()])
    : [];
  const pillarPath = subGroupPath.map((group) => group.name).join(' / ');

  const overviewButton = (
    <Button
      className='p-0'
      color='link'
      onClick={goToOverview}
      data-testid='survey-overview-breadcrumb-item'
      style={{ minWidth: 'fit-content' }}
    >
      {rootBreadcrumb.label}
    </Button>
  );

  return (
    <div className='question-container-breadcrumbs w-100 d-flex flex-wrap flex-md-nowrap overflow-hidden ml-2 text-ThemeTextPlaceholder'>
      {subGroupPath.length > 0 ? (
        <>
          <div className='question-container-breadcrumbs-standard d-flex mw-100'>
            {overviewButton}
            <div className='mx-2 flex-shrink-0'>&#47;</div>
            <div className='text-truncate' title={standardName}>
              {standardName}
            </div>
            <div className='mx-2 flex-shrink-0 d-none d-md-block'>&#47;</div>
          </div>
          {/* Own line below md; the pillar names truncate, the question code never does. */}
          <div className='question-container-breadcrumbs-path d-flex mw-100 fw-semibold'>
            <div className='text-truncate' title={pillarPath}>
              {pillarPath}
            </div>
            {standardCode ? (
              <>
                <div className='mx-2 flex-shrink-0'>&#47;</div>
                <div className='flex-shrink-0'>{standardCode}</div>
              </>
            ) : null}
          </div>
        </>
      ) : (
        <>
          {overviewButton}
          <div className='mx-2 flex-shrink-0'>&#47;</div>
          <div className='text-truncate flex-grow-1'>{`${standardName} ${standardCode ? '- ' + standardCode : ''} `}</div>
        </>
      )}
    </div>
  );
};
